import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { downloadFile, renderHeaderActionButtons, FilterProp, useAppDispatch, useAppSelector, SidePopover, FilterLayout, TableDashboard, CustomTooltipWithLabel } from '../../../globalUtils/globalExports';
import { Menu, MenuItem } from '@mui/material';
import './ManageSoftwareVersionReleasesDashboard.css';
import { versionReleasesTypes, getCurrentScreenAppliedFiltersCount, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, getVersionReleasesReducer, setFilterData, setRedirectedStateForVersionReleasesFilters, setRedirectedStateForDeploymentLogsFilters, fetchCustomerReferenceData } from '../../../rmsReduxStore/reduxExports';
import { cloneDeep } from 'lodash';
import { fetchVersionReleaseListData, setVersionReleaseTableProps, getCurrentFiltersValues, exportVersionReleaseListData } from '../../../rmsReduxStore/reduxExports';
import { Id, toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { setRedirectedStateForChargerFilters } from '../../../rmsReduxStore/chargersRedux/chargerCreators';
import {  formatRelativeTimeForTable, useEffectOnce } from '../../../globalUtils/globalHooks';
import access from '../../../auth/service/AccessControl';



function ManageSoftwareVersionReleasesDashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { versionReleases, versionReleasesCount, versionReleaseTableProps, versionReleaseTableListLoader, versionReleaseFiltersAppliedFromRedirection } = useAppSelector(getVersionReleasesReducer);
  const [exportAnchorEl, setExportAnchorEl] = useState(null)
  const { screenFilters } = useAppSelector(getFilterReducer)
  const [exportingFile, setExportingFile] = useState(false);
  const versionReleaseTablePropsRef = useRef(versionReleaseTableProps);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const { versionProductTypeReferenceData, versionClassificationReferenceData, versionSeverityReferenceData,versionReleasesCustomerReleaseTypeReferenceData,versionReleasesSwTypeReferenceData,customerReferenceData } = useAppSelector(getReferenceDataReducer)
  const versionReleasesRef = useRef(versionReleases)

  const handleNullValuesForFields = (value : string)=> {
        if(!value)return '-';
        else return value;
    }

  useEffectOnce(()=>{
    dispatch(fetchCustomerReferenceData());
  })

  const onDeployIconClick = (event, rowData,isAccessible) => {
    if(!isAccessible)return;
    if(!rowData?.ftpUrl || rowData?.ftpUrl.trim()===''){
      toast.error('No ftp url.');
      return;
    }
    navigate('/add-command', {
      state: {
        mode: 'SWRelease',
        commandData: {
          softwareVersion: rowData.softwareVersion,
          value: rowData.ftpUrl,
          productType: rowData.productType,
          swType: rowData.swType,
          productSubType: rowData.productSubType
        }
      }
    })
  }

  const onEditIconClick = (event,rowData,isAccessible) => {
            if(!isAccessible)return
            navigate('/add-version-release', {
              state: {
                mode: 'edit',
                versionData: {
                  id:rowData.id,
                  softwareVersion: rowData.softwareVersion,
                  url: rowData.ftpUrl,
                  description: rowData.description,
                  classification: rowData.classification,
                  severity: rowData.severity,
                  releaseDate: rowData.releaseDate,
                  productType: rowData.productType,
                  productSubType:rowData.productSubType,
                  swType:rowData.swType,
                  releaseDocument: rowData.releaseDocument,
                  customerRelease: rowData.customerRelease,
                  preSignedUrl:{
                    url:rowData?.fileUrl,
                    blobName:rowData.releaseDocument
                  }
                }
              }
            })
  }

  const actionIconsList = useCallback((releaseType) => {
    const deployAccess = releaseType==='Enabled'? access?.softwareVersionReleases?.deployment?.customerVersionDeploy() : access?.softwareVersionReleases?.deployment?.customerVersionDeploy();
    return (
      [
        {
        icon: deployAccess?'rms__version__release__settings__icon' : 'rms__version__release__deployment__disabled__icon',
        tooltipText:deployAccess ? 'Deploy' : 'Deployment unavailable — you don\'t have access rights.',
        fieldId:'version-releases-deploy-btn',
        className:'version-releases__deploy__icon__wrap',
        isAccessible: deployAccess,
        action: onDeployIconClick
        },
        {
        icon: 'rms__pencil__icon',
        tooltipText:'Edit',
        fieldId:'version-releases-edit-btn',
        className:'version-releases__edit__icon__wrap',
        isAccessible: access?.softwareVersionReleases?.addSoftwareVersion?.edit(),
        action: onEditIconClick
        }
      ]
    )

  }, [versionReleaseTablePropsRef?.current])

  const versionReleaseTableColumns = [
    {
      field: 'softwareVersion',
      headerName: 'Software Version',
      flex: 1,
      sortable: true,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <span
          style={{
            color: 'var(--secondary-blue-color)',
            fontWeight: 500,
            cursor: 'pointer'
          }}

          onClick={() => {
            if(!access?.softwareVersionReleases?.addSoftwareVersion?.view())return;
            const rowData = params.row
            navigate('/add-version-release', {
              state: {
                mode: 'view',
                versionData: {
                  id: rowData.id,
                  softwareVersion: rowData.softwareVersion,
                  url: rowData.ftpUrl,
                  description: rowData.description,
                  classification: rowData.classification,
                  severity: rowData.severity,
                  releaseDate: rowData.releaseDate,
                  productType: rowData.productType,
                  productSubType:rowData.productSubType,
                  swType:rowData.swType,
                  releaseDocument: rowData.releaseDocument,
                  customerRelease: rowData.customerRelease,
                  preSignedUrl:{
                    url:rowData?.fileUrl,
                    blobName:rowData.releaseDocument
                  }
                }
              }
            })
          }}
        >
          {handleNullValuesForFields(params.value)}
        </span>
        )
      }
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => {
        const subType = params.row?.productSubType ?? '';
        const productTye = params.row?.productType ?? '-'
        if(subType!=='' && productTye!=='-'){
          return (
            <span style={{ fontWeight: 500 }}>{productTye} - {subType}</span>
          )
        }else {
          <span style={{ fontWeight: 500 }}>{productTye}</span>
        }
        
      }
    },
    {
      field: 'swType',
      headerName: 'SW Type',
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params) => {
        const rowData = params?.row; 
        return (
          <CustomTooltipWithLabel label={rowData?.swType} labelId={'sw-type-'+rowData?.id} />
        )
      }
    },
    {
      field:'customerRelease',
      headerName:'Release Type',
      flex: 1,
      minWidth:120,
      sortable:true,
      renderCell:(params)=>{
        switch(params.value){
          case 'Enabled':
            return <span style={{ fontWeight: 500,color:'#008000',display:'flex',gap:'4px',alignItems:'center' }}> 
            <IconWithTooltip toolTipText='Customer live release' iconClassName='version__releases__customer__release-enabled-icon' containerClassName='version__releases__customer__release__icons' />
            Live
            </span>
          case 'Disabled':
            return <span style={{fontWeight:500, color:'#CA73E7',display:'flex',gap:'4px',alignItems:'center'}}>
              <IconWithTooltip toolTipText='Interal testing release' iconClassName='version__releases__customer__release-disabled-icon' containerClassName='version__releases__customer__release__icons' />
              Test
              </span>
        }
      }
    },
    {
      field: 'ftpUrl',
      headerName: 'FTP Software Upgrade Value(URL)',
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => {
        const rowData = params.row
        return (
            <CustomTooltipWithLabel label={rowData?.ftpUrl} labelId={rowData?.id} />
        )
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 250,
      flex: 2,
      sortable: false,
      renderCell: (params) => {
      const rowData = params.row
       return  (
       <CustomTooltipWithLabel label={rowData?.description} labelId={rowData?.id} />
       )
      }
    },
    {
      field: 'classification',
      headerName: 'Classification',
      minWidth: 100,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{handleNullValuesForFields(params.value)}</span>
      )
    },
    {
      field: 'severity',
      headerName: 'Severity',
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params) => {
        let color = ''
        let backgroundColor = ''
        switch (params.value) {
          case 'Critical':
            backgroundColor = '#ffcccc'; // light red
            color = '#b30000';           // dark red text
            break;

          case 'High':
            backgroundColor = '#ffe0b3'; // light orange
            color = '#b35900';           // dark orange text
            break;

          case 'Medium':
            backgroundColor = '#ffd966'; // warm amber
            color = '#7a4d00';           // deep amber/brown text
            break;

          case 'Low':
            backgroundColor = '#ccffcc'; // light green
            color = '#006600';           // dark green text
            break;
        }


        return <span
          style={{
            backgroundColor: backgroundColor,
            color: color,
            padding: '4px 10px',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '999px'
          }}
        >
          {params?.value ? params.value : '-'}
        </span>
      }
    },
    {
      field: 'releaseDate',
      headerName: 'Release Date',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{params?.value ? new Date(params?.value).toDateString() : '-'}</span>
      )
    },
    {
      field: 'firstDeployedOn',
      headerName: 'First Deployed On',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{params?.value ? new Date(params?.value).toDateString() : '-'}</span>
      )
    },
    {
      field: 'total',
      headerName: 'Total Chargers',
      renderHeader: ()=>{
        return (
          <span className='version-releases-column-header'>
            <span className='version-releases-column-header-text'>Total Chargers</span>
            <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Chargers belonging to the selected product type. Click the number to view the list of chargers.'}/></span>
          </span>
        )
      },
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: 'var(--secondary-blue-color)', cursor: 'pointer' }}
          onClick={async () => {
            const rowData = params.row
            await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
             const customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                id: customer?.id,
                value: customer?.value,
                label: customer?.label,
                name: customer?.name
              }))

            setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleases', customersToInclude,rowData.productType,null)(dispatch).then(res => {
              if (res === 'Action dispatched successfully') {
                navigate('/manage-chargers');
              }
            });
          }}
        >{params.value}</span>
      )
    },
    {
      field: 'totalUpdatedChargers',
      headerName: 'Updated Chargers',
      renderHeader: ()=>{
        return (
          <span className='version-releases-column-header'>
            <span className='version-releases-column-header-text'>Updated Chargers</span>
            <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Indicates chargers that have the latest software version. Click the number to view list of updated chargers.'}/></span>
          </span>
        )
      },
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: 'var(--secondary-blue-color)', cursor: 'pointer' }}
          onClick={async () => {
            const rowData = params.row
            await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
            const customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                id: customer?.id,
                value: customer?.value,
                label: customer?.label,
                name: customer?.name
              }))
            const isCCU = rowData?.swType === 'CCU';
            setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleases', customersToInclude,rowData.productType,rowData.softwareVersion,false,isCCU)(dispatch).then(res => {
              if (res === 'Action dispatched successfully') {
                navigate('/manage-chargers');
              }
            });
          }}
        >{params.value}</span>
      )

    },
    {
      field: 'outDatedChargers',
      headerName: 'Outdated Chargers',
      renderHeader: ()=>{
        return (
          <span className='version-releases-column-header'>
            <span className='version-releases-column-header-text'>Outdated Chargers</span>
            <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Chargers that are not on the latest software version (total chargers - updated chargers). Click the number to view the list of chargers.'}/></span>
          </span>
        )
      },
      flex: 1,
      minWidth: 185,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: 'var(--secondary-blue-color)', cursor: 'pointer' }}
        onClick={async () => {
            const rowData = params.row
            await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
            const customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                id: customer?.id,
                value: customer?.value,
                label: customer?.label,
                name: customer?.name
              }))
            const isCCU = rowData?.swType === 'CCU';
            setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleases', customersToInclude,rowData.productType,rowData.softwareVersion,true,isCCU)(dispatch).then(res => {
              if (res === 'Action dispatched successfully') {
                navigate('/manage-chargers');
              }
            });
          }}
        >{params.value}</span>
      )
    },
    {
      field: 'targetedChargers',
      headerName: 'Targeted Chargers',
      renderHeader: ()=>{
        return (
          <span className='version-releases-column-header'>
            <span className='version-releases-column-header-text'>Targeted Chargers</span>
            <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Chargers for which software deployment has been attempted via the Software Release module.'}/></span>
          </span>
        )
      },
      flex: 1,
      minWidth: 185,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: 'var(--secondary-blue-color)', cursor: 'pointer' }}
          onClick={() => {
            const rowData = params.row
            setRedirectedStateForDeploymentLogsFilters(true)(dispatch)
            const customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                id: customer?.id,
                value: customer?.value,
                label: customer?.label,
                name: customer?.name
              }))
            navigate('/manage-deployment-logs', {
              state: {
                productType: rowData.productType,
                softwareVersion: rowData.softwareVersion,
                customer: customersToInclude
              }
            })
          }}
        >{params.value}</span>
      )

    },
    {
      field: 'failedChargers',
      headerName: 'Failed Chargers',
      renderHeader: ()=>{
        return (
          <span className='version-releases-column-header'>
            <span className='version-releases-column-header-text'>Failed Chargers</span>
            <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Unique chargers with deployment failures. Click the number to view deployment logs.'}/></span>
          </span>
        )
      },
      flex: 1,
      minWidth: 165,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: 'var(--secondary-blue-color)', cursor: 'pointer' }}
          onClick={() => {
            const rowData = params.row
            setRedirectedStateForDeploymentLogsFilters(true)(dispatch)
            const customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                id: customer?.id,
                value: customer?.value,
                label: customer?.label,
                name: customer?.name
              }))
            navigate('/manage-deployment-logs', {
              state: {
                productType: rowData.productType,
                softwareVersion: rowData.softwareVersion,
                swLogStatus: 'Failed',
                customer: customersToInclude
              }
            })
          }}
        >{params.value}</span>
      )

    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{handleNullValuesForFields(params.value)}</span>
      )
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{handleNullValuesForFields(params.value)}</span>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{formatRelativeTimeForTable(params.value)}</span>
      )
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated On',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{formatRelativeTimeForTable(params.value)}</span>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      minWidth: 100,
      sortable: false,
      pinnable: false,
      renderCell: (params) => {
        const rowData = params?.row;
        const releaseType = rowData?.customerRelease
        return (
          <div className='version__releases__action__icon__wrap'>
            {
              actionIconsList(releaseType).map(actionBtn=>{
                if(!actionBtn?.isAccessible && actionBtn?.fieldId==='version-releases-edit-btn')return;
                return (
                  <div key={actionBtn.fieldId} className={actionBtn.className} onClick={(e)=>actionBtn.action(e,rowData,actionBtn?.isAccessible)}>
                    <IconWithTooltip  iconClassName={actionBtn.icon} toolTipText={actionBtn.tooltipText}/>
                  </div>
                )
              })
            }
          </div>
        )
      }
    }
  ]


  useEffect(() => {
    versionReleaseTablePropsRef.current = versionReleaseTableProps
  }, [versionReleaseTableProps])

  useEffect(() => {
    versionReleasesRef.current = versionReleases;
  }, [versionReleases])

  const layoutCallback = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      view: string,
      field: string | null,
      order: string,
      filterData,
      searchTerm: string,
    ) => {
      const tableProps = cloneDeep(versionReleaseTablePropsRef.current);
      const versionReleaseFilter = getCurrentFiltersValues('versionReleases', true);
      const appliedFilters = filterData ? filterData : versionReleaseFilter;

      await dispatch(
        fetchVersionReleaseListData({
          view: view ?? 'list',
          sortBy: field || tableProps?.sortBy,
          order: order || tableProps?.order,
          pageSize: pageSize,
          pageNumber: pageNumber,
          filters: appliedFilters,
          searchTerm: searchTerm != null ? searchTerm : tableProps?.searchTerm,
        })
      );

      if (tableProps) {
        tableProps.view = view || 'list'
        tableProps.sortBy = field || versionReleaseTablePropsRef?.current?.sortBy;
        tableProps.order = order || versionReleaseTablePropsRef?.current?.order;
        tableProps.pageNumber = pageNumber;
        tableProps.pageSize = pageSize;
        if (searchTerm != null && searchTerm != undefined) {
          tableProps.searchTerm = searchTerm;
        }
      }
      await dispatch(setVersionReleaseTableProps(tableProps));
    },
    [versionReleaseTablePropsRef?.current, dispatch]
  );

  //This function is use to handle the change of search text in the search box
  //It will update the table props with the new search text and call the layoutCallback to
  //fetch the data based on the new search text and other parameters.
  //It uses useCallback to memoize the function and avoid unnecessary re-renders.
  //It also uses cloneDeep to create a deep copy of the versionReleaseTablePropsRef
  //to avoid mutating the original object.
  //The function is dependent on the versionReleaseTablePropsRef and layoutCallback.
  const onChangeOfSearchText = useCallback(async (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    const tableProps = cloneDeep(versionReleaseTablePropsRef.current);
    if (tableProps) {
      tableProps.searchTerm = trimmedSearchText;
    }
    const versionReleaseFilter = getCurrentFiltersValues('versionReleases', true);
    await layoutCallback(
      1,
      tableProps?.pageSize,
      tableProps?.view,
      tableProps?.sortBy,
      tableProps?.order,
      versionReleaseFilter,
      trimmedSearchText,
    )
  }, [versionReleasesRef?.current, versionReleaseTablePropsRef?.current, layoutCallback]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchBoxClassName: 'version-releases__search__box',
      searchFieldId: 'version-releases-search-box',
      searchFieldName: 'version-releases-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText, versionReleaseTablePropsRef?.current]);


  //These are the functions to handle the export button click and close the export dropdown menu
  const handleClose = (): void => {
    setExportAnchorEl(null);

  };

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);

  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (exportingFile) {
      // already exporting a file, do not allow another export
      return;
    }
    try {
      const response = await dispatch(exportVersionReleaseListData({ fileType, filters: getCurrentFiltersValues('versionReleases', true) }));
      if (response?.status === 200 || response?.status === 202) {
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast();
      } else {
        fileDownloadFailureToast();
        setExportAnchorEl(null);
      }
    } finally {
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast]);


  //These are the header action buttons that will be rendered in the header of the software version releases page

  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_filter_btn',
          isAccessible: true,
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: getCurrentScreenAppliedFiltersCount('versionReleases',true)
        },
        {
          id: 'add_version_btn',
          isAccessible: access?.softwareVersionReleases?.addSoftwareVersion?.add(),
          headerActionBtnClick: (): void => {
            navigate('/add-version-release');
          }
        },
        {
          id: 'export_csv_btn',
          isAccessible: true,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          headerActionBtnClick: (event): void => {
            setExportAnchorEl(event.currentTarget);
          },
        },
      ],
  })


  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }

  const onResetButtonClick = useCallback(async (callList: boolean) => {
    const res = await setFilterData(null, '', 'versionReleases', 'CLEAR', true)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      // Reset the filters
      if (callList && versionReleaseTablePropsRef.current) {
        const tableProps = cloneDeep(versionReleaseTablePropsRef.current);
        await layoutCallback(
          1,
          tableProps?.pageSize,
          tableProps?.view,
          tableProps?.sortBy,
          tableProps?.order,
          res?.filters,
          tableProps?.searchTerm ?? '',
        )
      }
    }

  }, [versionReleaseTablePropsRef.current])

  const filterFooterBtns = useMemo(() => {
    return [
      {
        buttonText: 'Reset',
        buttonId: 'Reset',
        btnClassName: 'secondary__btn',
        handleClick: (): Promise<void> => onResetButtonClick(true),
        isDisabled: false,
        buttonVariant: 'outlined',
      },
    ];
  }, [onResetButtonClick]);

  const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
    const tableProps = cloneDeep(versionReleaseTablePropsRef.current);
    const res = await setFilterData(data, filterKey, 'versionReleases', 'SET', isGlobal, true)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      await layoutCallback?.(
        1,
        tableProps?.pageSize,
        tableProps?.view,
        tableProps?.sortBy,
        tableProps?.order,
        res?.filters,
        tableProps?.searchTerm ?? '',
      )
    }
  }, [versionReleaseTablePropsRef?.current, versionReleasesRef?.current]);



  const versionReleasesFilterProps: FilterProp[] = useMemo(() => {
    const filterReleaseDateValue = getFilterSelectedValue('versionReleaseDateInterval', 'versionReleases', false);
    return [
      {
        filterLabel: 'Product Type',
        filterId: 'versionProductType',
        filterType: 'dropdown' as 'dropdown' | 'dateFilter',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('versionProductType', 'versionReleases', false),
          selectOptions: versionProductTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'versionProductType', false)
          },
          selectDropDownId: 'version-releases-product-type-filter-dropdown',
          isMultiSelect: true
        }
      },
      {
        filterLabel: 'SW Type',
        filterId: 'versionSwType',
        filterType: 'dropdown' as 'dropdown' | 'dateFilter',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('versionSwType', 'versionReleases', false),
          selectOptions: versionReleasesSwTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'versionSwType', false)
          },
          selectDropDownId: 'version-releases-sw-type-filter-dropdown',
          isMultiSelect: true
        }
      },
      {
        filterLabel: 'Release Type',
        filterId: 'versionReleaseType',
        filterType: 'dropdown' as 'dropdown' | 'dateFilter',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('versionReleaseType', 'versionReleases', false),
          selectOptions: versionReleasesCustomerReleaseTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'versionReleaseType', false)
          },
          selectDropDownId: 'version-releases-release-type-filter-dropdown',
          isMultiSelect: true
        }
      },
      {
        filterLabel: 'Severity',
        filterId: 'versionSeverity',
        filterType: 'dropdown' as 'dropdown' | 'dateFilter',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('versionSeverity', 'versionReleases', false),
          selectOptions: versionSeverityReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'versionSeverity', false)
          },
          selectDropDownId: 'version-releases-severity-filter-dropdown',
          isMultiSelect: true
        }
      },
      {
        filterLabel: 'Classfication',
        filterId: 'versionClassification',
        filterType: 'dropdown' as 'dropdown' | 'dateFilter',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('versionClassification', 'versionReleases', false),
          selectOptions: versionClassificationReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'versionClassification', false)
          },
          selectDropDownId: 'version-releases-classification-filter-dropdown',
          isMultiSelect: true
        }
      },

      {
        filterLabel: 'Release Date',
        filterId: 'veresionReleaseDateInterval',
        filterType: 'dateFilter' as 'dropdown' | 'dateFilter',
        dateFilterProps: {
          datePickerId: 'version-releases-release-date-picker',
          datePickerClassName: '',
          selectsRange: true,
          startDate: filterReleaseDateValue?.[0]
            ? new Date(filterReleaseDateValue?.[0])
            : null,
          endDate: filterReleaseDateValue?.[1]
            ? new Date(filterReleaseDateValue?.[1])
            : null,
          onChange: (val): void => {
            handleFilterDataChange(val, 'versionReleaseDateInterval', false);
          },
        }
      }
    ]
  }, [versionProductTypeReferenceData, versionClassificationReferenceData, versionSeverityReferenceData, handleFilterDataChange, screenFilters,versionReleasesCustomerReleaseTypeReferenceData,versionReleasesSwTypeReferenceData]);

  const getTableRows = useCallback((): IVersionRelease[] => {
    return versionReleases !== null && versionReleases?.length > 0 ? versionReleases : []
  }, [versionReleases])


  //Clearing the redux states
  const versionReleasesListCleanUpState = useCallback(() => {
    const action = {
      type: versionReleasesTypes.CLEAR_VERSION_RELEASE_DATA,
    }
    dispatch(action)
    if (versionReleaseFiltersAppliedFromRedirection) {
      onResetButtonClick(false)
    }
    setRedirectedStateForVersionReleasesFilters(false)(dispatch)
  }, [])

  useEffect(() => {
    window.addEventListener('beforeunload', versionReleasesListCleanUpState);
    return (): void => {
      versionReleasesListCleanUpState()
      window.removeEventListener('beforeunload', versionReleasesListCleanUpState)
    }
  }, [])
  return (
    <>
      <TableDashboard
        layoutsEnabled={true}
        tableId="manage-version-releases-table"
        tableClassName="manage__version__releases__table"
        header='Software Version Releases'
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={versionReleaseTableColumns}
        tableRows={getTableRows()}
        layoutView='list'
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={versionReleaseTableListLoader ? 0 : versionReleasesCount}
        showSkeleton={!versionReleases}
        totalCountText={'version releases'}
        showLoader={versionReleaseTableListLoader}
        pageNumber={versionReleaseTableProps?.pageNumber}
        tablePageSize={versionReleaseTableProps?.pageSize}
        sortField={versionReleaseTableProps?.sortBy}
        sortOrder={versionReleaseTableProps?.order}
        showRefreshButton={false}
        leftPinnedColumns={['softwareVersion','productType']}
      />

      <Menu
        id="export-option-dropdown"
        anchorEl={exportAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(exportAnchorEl)}
        onClose={handleClose}
      >
        <MenuItem>
          <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('csv') }}>
            To CSV
          </div>
        </MenuItem>
        <MenuItem>

          <div className='export__btn-option' onClick={(e) => {
            e?.stopPropagation();
            onExportBtnClick('xlsx')
          }}>
            To Excel
          </div>
        </MenuItem>
      </Menu>

      {/* This is filter pop over */}
      <>
        {
          filtersVisible && <SidePopover
            id={'manage-version-releases-filters'}
            open={filtersVisible}
            containerClassName={'version-releases-side-filters'}
            handleClose={handleFiltersClosePopOver}
            headerText={'Add Filters'}
            // eslint-disable-next-line react/no-children-prop
            children={<FilterLayout requiredReferenceDataCallbacks={['versionSeverity', 'versionClassification', 'versionProductType','versionReleaseType','versionSwType']}
              filters={versionReleasesFilterProps} handleFilterDataChange={handleFilterDataChange}
              excludeGlobalFilters={true}
            />}
            btnsList={filterFooterBtns}
          />
        }
      </>
    </>
  )
}

export default ManageSoftwareVersionReleasesDashboard;